import React, { useContext } from 'react';
import Hidden from '@material-ui/core/Hidden';
import { Stack, Text } from '@solvhealth/jigsaw-v1';
import { useDispatch } from 'react-redux';
import { analyticsTrackEvent } from '~/core/analytics/index';
import {
  COVID_SERVICE_COPY_MAP,
  CovidTestSlugs,
  getResultTimeCopy,
  getResultTimeCopyV2,
  INSURANCE_ACCEPTED,
  INSURANCE_NOT_ACCEPTED,
} from '../util';
import { setReasonForVisit } from '~/actions/newBooking';
import {
  CDP_COVID_TEST_SECTION_ACCORDION_CLICK,
  CDP_COVID_TEST_SECTION_BOOK,
} from '~/core/analytics/events';
import { formatPrice } from '~/core/util/string';
import { LocationContext } from '../../../ContextProviders';
import { StyledSpanBody, StyledUl } from '../CSSstyles';
import CovidTestSection from './CovidTestSection';
import BorderBoxReset from '~/components/util/BorderBoxReset';
import dataTestIds from '~/test/test-ids';
import CDPServiceAccordion from '~/components/ClinicDetailPageFlexible/components/CDPServiceAccordion/CDPServiceAccordion';
import { isMobile, useUserAgent } from '~/core/util/device';
import { useStartBooking } from '~/components/ClinicDetailPageFlexible/hooks/useStartBooking';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';

// this func could live in the component and we wouldn't need to pass props in
const getPriceText = (
  cashAccepted: boolean,
  price: string,
  minPrice: string,
  maxPrice: string,
  hasPriceRange: boolean
) => {
  if (cashAccepted) {
    if (hasPriceRange && minPrice !== maxPrice) {
      return `${formatPrice(minPrice, 2, true, true)}-${formatPrice(
        maxPrice,
        2,
        true,
        true,
        false
      )}`;
    }
    if (price) {
      return `${formatPrice(price, 2, true, true)}`;
    }
  }

  return '';
};

const renderScreeningRequiredBullet = (
  <li>
    <Text variant="body2">
      A <strong>screening may be required</strong> to determine if you're eligible. The clinic will
      follow up with you after completing your booking.
    </Text>
  </li>
);

type Props = {
  data: any;
};

const CovidTestAccordion = (props: Props) => {
  const { location } = useContext(LocationContext);
  const { id, uclId } = location;
  const { data } = props;

  const userAgent = useUserAgent();
  const dispatch = useDispatch();
  const startBooking = useStartBooking();

  if (!data) return <div />;

  const analyticsOptions = (test: string, type: string, platform?: string) => {
    return {
      action: `${type} ${test}${platform ? ` in ${platform}` : ''}`,
      locationId: id,
      uclId,
    };
  };

  const onBookTestClick = (test: string) => {
    dispatch(setReasonForVisit(test));
    analyticsTrackEvent(
      CDP_COVID_TEST_SECTION_BOOK,
      analyticsOptions(test, 'Book', isMobile(userAgent) ? 'mobile' : 'desktop')
    );
    startBooking();
  };

  const handleAccordionClick = (test: string) => {
    analyticsTrackEvent(CDP_COVID_TEST_SECTION_ACCORDION_CLICK, analyticsOptions(test, 'Click'));
  };

  const {
    screeningRequired,
    walkinAccepted,
    insuranceAccepted,
    cashAccepted,
    price,
    minPrice,
    maxPrice,
    hasPriceRange,
    timeToResult,
    notes,
    type,
  } = data;

  const { title } = COVID_SERVICE_COPY_MAP[type];

  const getTimeToResultText = (time: string) => {
    let resultTimeCopy: string = getResultTimeCopyV2(time);
    let needsSuperscript: boolean;
    if (resultTimeCopy === 'Prefer not to say' || !resultTimeCopy) {
      return '';
    }
    resultTimeCopy = ` · ${resultTimeCopy}`;
    needsSuperscript = true;

    if (needsSuperscript) resultTimeCopy += '¹';

    return resultTimeCopy;
  };

  const getOldServiceTimeToResultText = (details: any) => {
    const timeCopy = getResultTimeCopy(details, 'badgeCopy');
    if (!timeCopy) return '';
    return ` · ${timeCopy}`;
  };

  // TODO: clean up this logic- lots of nested functions calls
  // the issue appears to be that there are two different data formats for 'result time'
  // we need to handle
  const getTimeText = () => {
    return title === COVID_SERVICE_COPY_MAP[CovidTestSlugs.COVID_TEST].title
      ? getOldServiceTimeToResultText(data)
      : getTimeToResultText(timeToResult);
  };

  const renderWalkInBullet = (walkinAccepted: boolean) => {
    if (walkinAccepted) {
      return (
        <>
          <Hidden smDown>
            <li>
              <Text variant="body2">
                To book, enter "
                <StyledSpanBody onClick={() => onBookTestClick(title)}>{title}</StyledSpanBody>" as
                your reason for visit and complete the booking. Walk-ins are also accepted.
              </Text>
            </li>
          </Hidden>
          <Hidden mdUp>
            <li>
              <Text variant="body2">Walk-ins are accepted.</Text>
            </li>
          </Hidden>
        </>
      );
    }
    return (
      <>
        <Hidden smDown>
          <li>
            <Text variant="body2">
              To book, enter "
              <StyledSpanBody onClick={() => onBookTestClick(title)}>{title}</StyledSpanBody>" as
              your reason for visit and complete the booking.
            </Text>
          </li>
        </Hidden>
      </>
    );
  };

  const renderAccordionBody = () => {
    if (title === 'COVID test') {
      return <CovidTestSection testDetails={data} testType={CovidTestSlugs.COVID_TEST} />;
    }
    return (
      <Stack space={1}>
        <Text variant="body2">{COVID_SERVICE_COPY_MAP[type].definition}</Text>
        <StyledUl>
          {screeningRequired && renderScreeningRequiredBullet}
          <li>
            <Text variant="body2">
              {insuranceAccepted ? INSURANCE_ACCEPTED : INSURANCE_NOT_ACCEPTED}
            </Text>
          </li>
          <li>
            <Text variant="body2">{COVID_SERVICE_COPY_MAP[type].explanation}</Text>
          </li>
          {renderWalkInBullet(walkinAccepted)}
        </StyledUl>
        {notes && notes.length && (
          <Text variant="body2">
            <strong>Note from clinic: </strong>
            {notes}
          </Text>
        )}
      </Stack>
    );
  };

  const timeText = getTimeText();

  const selfPayPrice = getPriceText(cashAccepted, price, minPrice, maxPrice, hasPriceRange);

  return (
    <BorderBoxReset>
      <CDPServiceAccordion
        body={renderAccordionBody()}
        data-testid={`${dataTestIds.cdp.covidTestAccordion}-${title}`}
        onClick={() => handleAccordionClick(title)}
        selfPayPrice={selfPayPrice}
        subtitle={timeText}
        title={title}
      />
    </BorderBoxReset>
  );
};

export default CovidTestAccordion;
